import { useCallback, useEffect, useState } from 'react';

import { alterarCampo, excluirItem, incluirAtividade, incluirPergunta, listarCampos } from '../../conteudo/api/conteudo';
import type { CampoConteudo, CamposDaFase, ItemDaEstrutura, NovaPergunta } from '../../conteudo/api/conteudo';
import { alterarRegra, listarRegras } from '../../pontuacao/api/pontuacao';
import type { RegrasDaFase, ValorDaRegra } from '../../pontuacao/api/pontuacao';
import { LIXEIRA, mensagemDaFalha, SETA, valorTipado } from './comum';
import type { Aviso } from './comum';

/** O teto do servidor (`MAXIMO_DE_PERGUNTAS`): até 8 perguntas comuns no Laboratório, fora a bônus. */
const MAXIMO_DE_PERGUNTAS = 8;
const LETRAS = ['A', 'B', 'C', 'D'] as const;
type Letra = (typeof LETRAS)[number];

interface OpcaoDaTela {
    letra: Letra;
    texto: CampoConteudo | undefined;
    correta: CampoConteudo | undefined;
}

interface PerguntaDaTela {
    id: string;
    /** `null` é a pergunta bônus. */
    k: number | null;
    enunciado: CampoConteudo | undefined;
    pontos: CampoConteudo | undefined;
    opcoes: OpcaoDaTela[];
    item: ItemDaEstrutura | undefined;
}

interface AtividadeDaTela {
    id: string;
    n: number;
    titulo: CampoConteudo | undefined;
    tempo: CampoConteudo | undefined;
    abertura: CampoConteudo | undefined;
    fim: CampoConteudo | undefined;
    comuns: PerguntaDaTela[];
    bonus: PerguntaDaTela | undefined;
    item: ItemDaEstrutura | undefined;
}

/** Atividades, perguntas e alternativas, a partir do `local` ("Atividade N › Pergunta k › Opção X"). */
function montarAtividades(dados: CamposDaFase): AtividadeDaTela[] {
    const itens = new Map((dados.estrutura?.itens ?? []).map((item) => [item.id, item]));
    const doLocal = (local: string) => dados.campos.filter((campo) => campo.local === local);
    const atividades = new Map<string, AtividadeDaTela>();
    for (const campo of dados.campos) {
        if (campo.entidade !== 'atividade' || atividades.has(campo.id)) continue;
        const local = campo.local;
        const proprio = (nome: string) => doLocal(local).find((c) => c.entidade === 'atividade' && c.campo === nome);
        const perguntas = new Map<string, PerguntaDaTela>();
        for (const outro of dados.campos) {
            if (outro.entidade !== 'pergunta' || !outro.local.startsWith(`${local} › Pergunta `)) continue;
            if (perguntas.has(outro.id)) continue;
            const sufixo = outro.local.slice(`${local} › Pergunta `.length);
            const daPergunta = doLocal(outro.local);
            perguntas.set(outro.id, {
                id: outro.id,
                k: sufixo === 'bônus' ? null : Number(sufixo),
                enunciado: daPergunta.find((c) => c.campo === 'enunciado'),
                pontos: daPergunta.find((c) => c.campo === 'pontos'),
                opcoes: LETRAS.map((letra) => {
                    const daOpcao = doLocal(`${outro.local} › Opção ${letra}`);
                    return { letra, texto: daOpcao.find((c) => c.campo === 'texto'), correta: daOpcao.find((c) => c.campo === 'correta') };
                }),
                item: itens.get(outro.id),
            });
        }
        const todas = [...perguntas.values()];
        atividades.set(campo.id, {
            id: campo.id,
            n: Number(local.replace('Atividade ', '')),
            titulo: proprio('titulo'),
            tempo: proprio('tempo_segundos'),
            abertura: proprio('modal_inicio'),
            fim: proprio('modal_fim'),
            comuns: todas.filter((p) => p.k !== null).sort((a, b) => (a.k ?? 0) - (b.k ?? 0)),
            bonus: todas.find((p) => p.k === null),
            item: itens.get(campo.id),
        });
    }
    return [...atividades.values()].sort((a, b) => a.n - b.n);
}

const chaveDoCampo = (campo: CampoConteudo) => `${campo.entidade}:${campo.id}:${campo.campo}`;
const letraCorreta = (pergunta: PerguntaDaTela): Letra | '' =>
    pergunta.opcoes.find((opcao) => opcao.correta?.valor === true)?.letra ?? '';

/** Pergunta ainda não criada: vai ao servidor no "Salvar Alterações". */
interface PerguntaNova {
    enunciado: string;
    pontos: string;
    opcoes: string[];
    correta: Letra | '';
}

interface AtividadeNova {
    tempId: string;
    titulo: string;
    tempo: string;
    quantidade: number;
    perguntas: PerguntaNova[];
}

const perguntaVazia = (): PerguntaNova => ({ enunciado: '', pontos: '1', opcoes: ['', '', '', ''], correta: '' });
const perguntaCompleta = (pergunta: PerguntaNova) =>
    pergunta.enunciado.trim() !== '' && pergunta.opcoes.every((opcao) => opcao.trim() !== '') && pergunta.correta !== ''
    && pergunta.pontos.trim() !== '';
const paraOServidor = (pergunta: PerguntaNova): NovaPergunta => ({
    enunciado: pergunta.enunciado.trim(),
    pontos: Number(pergunta.pontos),
    opcoes: pergunta.opcoes.map((opcao) => opcao.trim()),
    correta: pergunta.correta as Letra,
});

interface CartaoPerguntaProps {
    titulo: string;
    idBase: string;
    valores: PerguntaNova;
    travado: boolean;
    aoMudar: (mudanca: Partial<PerguntaNova>) => void;
    lixeira?: { rotulo: string; aoExcluir: () => void };
}

/** A pergunta com os pontos e as quatro alternativas; o rádio marca a correta. */
function CartaoPergunta({ titulo, idBase, valores, travado, aoMudar, lixeira }: CartaoPerguntaProps) {
    return (
        <section className="cartao-pista" aria-labelledby={`${idBase}-titulo`}>
            <div className="cabecalho-cartao-config">
                <h4 id={`${idBase}-titulo`}>{titulo}</h4>
                {lixeira && (
                    <button type="button" className="lixeira-config" aria-label={lixeira.rotulo} onClick={lixeira.aoExcluir}>
                        {LIXEIRA}
                    </button>
                )}
            </div>
            <label className="campo-config">
                Pergunta
                <textarea rows={3} value={valores.enunciado} disabled={travado} onChange={(evento) => aoMudar({ enunciado: evento.target.value })} />
            </label>
            <label className="campo-config campo-config-curto">
                Pontos
                <input type="number" min={0} value={valores.pontos} disabled={travado} onChange={(evento) => aoMudar({ pontos: evento.target.value })} />
            </label>
            <div className="alternativas-config" role="group" aria-label="Alternativas">
                <p className="rotulo-alternativas">Alternativas <span>(marque a correta)</span></p>
                {LETRAS.map((letra, i) => (
                    <div key={letra} className="alternativa-config">
                        <input
                            type="radio"
                            name={`${idBase}-correta`}
                            aria-label={`Correta ${letra}`}
                            checked={valores.correta === letra}
                            disabled={travado}
                            onChange={() => aoMudar({ correta: letra })}
                        />
                        <span className="letra-alternativa" aria-hidden="true">{letra}</span>
                        <input
                            type="text"
                            aria-label={`Alternativa ${letra}`}
                            value={valores.opcoes[i]}
                            disabled={travado}
                            onChange={(evento) => aoMudar({ opcoes: valores.opcoes.map((texto, j) => (j === i ? evento.target.value : texto)) })}
                        />
                    </div>
                ))}
            </div>
        </section>
    );
}

/**
 * A aba Laboratório de Pixels de Configurações, no padrão do Scan Pixel: seletor de atividade com
 * "Nova Atividade", a atividade marcada com as perguntas e alternativas, e a Pontuação. Tudo fica
 * pendente até "Salvar Alterações"; as lixeiras confirmam e valem na hora.
 */
export function LabPixelsConfiguracao({ csrfToken }: { csrfToken: string }) {
    const [dados, setDados] = useState<CamposDaFase | null>(null);
    const [regras, setRegras] = useState<RegrasDaFase | null>(null);
    const [falhouCarregar, setFalhouCarregar] = useState(false);

    const [rascunho, setRascunho] = useState<Record<string, string>>({});
    const [corretas, setCorretas] = useState<Record<string, Letra>>({});
    const [quantidade, setQuantidade] = useState<Record<string, number>>({});
    const [perguntasNovas, setPerguntasNovas] = useState<Record<string, PerguntaNova>>({});
    const [atividadesNovas, setAtividadesNovas] = useState<AtividadeNova[]>([]);
    const [regrasRascunho, setRegrasRascunho] = useState<Record<string, string>>({});
    const [selecionada, setSelecionada] = useState(1);

    const [pontuacaoAberta, setPontuacaoAberta] = useState(true);
    const [salvando, setSalvando] = useState(false);
    const [aviso, setAviso] = useState<Aviso | null>(null);

    const carregar = useCallback(async () => {
        try {
            const [conteudo, todasAsRegras] = await Promise.all([listarCampos('labpixels'), listarRegras()]);
            setDados(conteudo);
            setRegras(todasAsRegras.find((fase) => fase.fase === 'labpixels') ?? null);
            setFalhouCarregar(false);
        } catch {
            setFalhouCarregar(true);
        }
    }, []);

    useEffect(() => {
        void carregar();
    }, [carregar]);

    function descartar() {
        setRascunho({});
        setCorretas({});
        setQuantidade({});
        setPerguntasNovas({});
        setAtividadesNovas([]);
        setRegrasRascunho({});
    }

    if (falhouCarregar) {
        return (
            <p className="estado-variaveis mensagem-login">
                Não foi possível carregar o Laboratório de Pixels.{' '}
                <button className="botao-texto" type="button" onClick={() => void carregar()}>Tentar novamente</button>
            </p>
        );
    }
    if (!dados || !regras) return <p className="estado-variaveis">Carregando o Laboratório de Pixels…</p>;

    const atividades = montarAtividades(dados);
    const travado = !dados.editavel;
    const regrasTravadas = !regras.editavel;
    const abertas = [...new Set([...dados.turmas_com_fase_aberta, ...regras.turmas_com_fase_aberta])];
    const totalDeAtividades = atividades.length + atividadesNovas.length;
    const marcada = selecionada >= 1 && selecionada <= totalDeAtividades ? selecionada : 1;

    const valorDoCampo = (campo: CampoConteudo | undefined) =>
        campo ? (rascunho[chaveDoCampo(campo)] ?? (campo.valor === null ? '' : String(campo.valor))) : '';
    const mudarCampo = (campo: CampoConteudo | undefined, valor: string) => {
        if (campo) setRascunho((atual) => ({ ...atual, [chaveDoCampo(campo)]: valor }));
    };
    const quantidadeDe = (atividade: AtividadeDaTela) => quantidade[atividade.id] ?? atividade.comuns.length;
    const somaDesejada = atividades.reduce((soma, atividade) => soma + quantidadeDe(atividade), 0)
        + atividadesNovas.reduce((soma, nova) => soma + nova.quantidade, 0);
    const tetoDaAtividade = (atual: number) => Math.max(atual, Math.min(MAXIMO_DE_PERGUNTAS, atual + MAXIMO_DE_PERGUNTAS - somaDesejada));
    const valorDaRegra = (chave: string) => regrasRascunho[chave] ?? String(regras.regras[chave] ?? '');
    const chaveDaNova = (atividadeId: string, k: number) => `${atividadeId}:${k}`;

    /** Os valores de uma pergunta existente na tela, com o que já foi digitado. */
    const valoresDaPergunta = (pergunta: PerguntaDaTela): PerguntaNova => ({
        enunciado: valorDoCampo(pergunta.enunciado),
        pontos: valorDoCampo(pergunta.pontos),
        opcoes: pergunta.opcoes.map((opcao) => valorDoCampo(opcao.texto)),
        correta: corretas[pergunta.id] ?? letraCorreta(pergunta),
    });
    const mudarPergunta = (pergunta: PerguntaDaTela, mudanca: Partial<PerguntaNova>) => {
        if (mudanca.enunciado !== undefined) mudarCampo(pergunta.enunciado, mudanca.enunciado);
        if (mudanca.pontos !== undefined) mudarCampo(pergunta.pontos, mudanca.pontos);
        if (mudanca.opcoes) mudanca.opcoes.forEach((texto, i) => {
            if (texto !== valoresDaPergunta(pergunta).opcoes[i]) mudarCampo(pergunta.opcoes[i].texto, texto);
        });
        if (mudanca.correta) setCorretas((atual) => ({ ...atual, [pergunta.id]: mudanca.correta as Letra }));
    };

    async function excluirNaHora(pergunta: string, acao: () => Promise<void>) {
        if (!window.confirm(pergunta)) return;
        setAviso(null);
        try {
            await acao();
            setAviso({ tipo: 'sucesso', texto: 'Excluído.' });
        } catch (erro) {
            setAviso({ tipo: 'erro', texto: mensagemDaFalha(erro) });
        }
        await carregar();
    }

    async function salvar() {
        if (!dados || !regras) return;
        setAviso(null);

        const novas: { atividade: AtividadeDaTela; pergunta: PerguntaNova }[] = [];
        const removidas: { atividade: AtividadeDaTela; pergunta: PerguntaDaTela }[] = [];
        for (const atividade of atividades) {
            const desejada = quantidadeDe(atividade);
            for (let k = atividade.comuns.length + 1; k <= desejada; k++) {
                const nova = perguntasNovas[chaveDaNova(atividade.id, k)] ?? perguntaVazia();
                if (!perguntaCompleta(nova)) {
                    setSelecionada(atividade.n);
                    setAviso({
                        tipo: 'erro',
                        texto: `Preencha a Pergunta ${k} da Atividade ${atividade.n}: o enunciado, as quatro alternativas e a correta.`,
                    });
                    return;
                }
                novas.push({ atividade, pergunta: nova });
            }
            for (const pergunta of atividade.comuns.slice(desejada)) removidas.push({ atividade, pergunta });
        }
        for (const [indice, nova] of atividadesNovas.entries()) {
            if (!nova.titulo.trim() || !nova.perguntas.slice(0, nova.quantidade).every(perguntaCompleta)) {
                setSelecionada(atividades.length + indice + 1);
                setAviso({ tipo: 'erro', texto: `Preencha o título e as perguntas da Atividade ${atividades.length + indice + 1}.` });
                return;
            }
        }
        if (removidas.length > 0) {
            const lista = removidas.map(({ atividade, pergunta }) => `Pergunta ${pergunta.k} da Atividade ${atividade.n}`).join(', ');
            if (!window.confirm(`Remover ${lista}? A pergunta é apagada e isso não pode ser desfeito.`)) return;
        }

        setSalvando(true);
        try {
            for (const campo of dados.campos) {
                const digitado = rascunho[chaveDoCampo(campo)];
                const original = campo.valor === null ? '' : String(campo.valor);
                if (digitado === undefined || digitado === original) continue;
                await alterarCampo(campo, valorTipado(campo, digitado), campo.valor, csrfToken);
            }
            // Marcar a nova correta desmarca a anterior no servidor (`RN-CONT-006`).
            for (const atividade of atividades) {
                for (const pergunta of [...atividade.comuns, ...(atividade.bonus ? [atividade.bonus] : [])]) {
                    const escolhida = corretas[pergunta.id];
                    if (!escolhida || escolhida === letraCorreta(pergunta)) continue;
                    const campo = pergunta.opcoes.find((opcao) => opcao.letra === escolhida)?.correta;
                    if (campo) await alterarCampo(campo, true, false, csrfToken);
                }
            }
            for (const { pergunta } of removidas) await excluirItem({ entidade: 'pergunta', id: pergunta.id }, csrfToken);
            for (const { atividade, pergunta } of novas) await incluirPergunta(atividade.id, paraOServidor(pergunta), csrfToken);
            for (const nova of atividadesNovas) {
                const [primeira, ...demais] = nova.perguntas.slice(0, nova.quantidade);
                const criada = await incluirAtividade(
                    {
                        titulo: nova.titulo.trim(),
                        tempo_segundos: nova.tempo.trim() === '' ? null : Number(nova.tempo),
                        pergunta: paraOServidor(primeira),
                    },
                    csrfToken,
                );
                for (const pergunta of demais) await incluirPergunta(criada.id, paraOServidor(pergunta), csrfToken);
            }
            for (const [chave, digitado] of Object.entries(regrasRascunho)) {
                if (digitado === String(regras.regras[chave] ?? '')) continue;
                await alterarRegra('labpixels', chave, digitado as ValorDaRegra, csrfToken);
            }
            descartar();
            setAviso({ tipo: 'sucesso', texto: 'Alterações salvas.' });
        } catch (erro) {
            setAviso({ tipo: 'erro', texto: mensagemDaFalha(erro) });
        } finally {
            setSalvando(false);
            await carregar();
        }
    }

    const mudarAtividadeNova = (tempId: string, mudanca: (nova: AtividadeNova) => AtividadeNova) =>
        setAtividadesNovas((atual) => atual.map((nova) => (nova.tempId === tempId ? mudanca(nova) : nova)));
    const podeIncluirAtividade = !travado && dados.estrutura?.pode_incluir !== false && somaDesejada < MAXIMO_DE_PERGUNTAS;
    const motivoSemIncluir = dados.estrutura?.motivo_incluir
        ?? (somaDesejada >= MAXIMO_DE_PERGUNTAS ? `O Laboratório tem no máximo ${MAXIMO_DE_PERGUNTAS} perguntas, fora a bônus.` : undefined);

    const seletorDeQuantidade = (valor: number, aoMudar: (quantidadeNova: number) => void) => (
        <label className="campo-config campo-config-curto">
            Quantidade de perguntas
            <select value={String(valor)} disabled={travado} onChange={(evento) => aoMudar(Number(evento.target.value))}>
                {Array.from({ length: tetoDaAtividade(valor) }, (_, i) => (
                    <option key={i + 1} value={String(i + 1)}>{i + 1}</option>
                ))}
            </select>
        </label>
    );

    return (
        <div className="config-fase">
            {abertas.length > 0 && (
                <p className="aviso-fase-aberta" role="status">
                    A fase Laboratório de Pixels está aberta na turma {abertas.join(', ')}: só leitura até ela fechar.
                </p>
            )}

            <div className="seletor-enigma-config">
                <label>
                    <span>Atividade:</span>
                    <select aria-label="Atividade" value={String(marcada)} onChange={(evento) => setSelecionada(Number(evento.target.value))}>
                        {Array.from({ length: totalDeAtividades }, (_, i) => (
                            <option key={i + 1} value={String(i + 1)}>{i + 1}</option>
                        ))}
                    </select>
                </label>
                <button
                    type="button"
                    className="botao-novo-config"
                    disabled={!podeIncluirAtividade}
                    title={podeIncluirAtividade ? undefined : motivoSemIncluir}
                    onClick={() => {
                        setAtividadesNovas((atual) => [
                            ...atual,
                            { tempId: `atividade-${Date.now()}-${atual.length}`, titulo: '', tempo: '', quantidade: 1, perguntas: [perguntaVazia()] },
                        ]);
                        setSelecionada(totalDeAtividades + 1);
                    }}
                >
                    <span aria-hidden="true">+</span> Nova Atividade
                </button>
            </div>

            {atividades.filter((atividade) => atividade.n === marcada).map((atividade) => {
                const desejada = quantidadeDe(atividade);
                return (
                    <section key={atividade.id} className="cartao-config" aria-labelledby={`atividade-${atividade.id}`}>
                        <div className="cabecalho-cartao-config">
                            <h3 id={`atividade-${atividade.id}`}>Atividade {atividade.n}</h3>
                            {atividade.item?.pode_excluir && !travado && (
                                <button
                                    type="button"
                                    className="lixeira-config"
                                    aria-label={`Excluir Atividade ${atividade.n}`}
                                    onClick={() => void excluirNaHora(
                                        `Excluir a Atividade ${atividade.n} e as perguntas dela? Isso não pode ser desfeito.`,
                                        () => excluirItem({ entidade: 'atividade', id: atividade.id }, csrfToken),
                                    )}
                                >
                                    {LIXEIRA}
                                </button>
                            )}
                        </div>
                        <label className="campo-config">
                            Título
                            <input type="text" value={valorDoCampo(atividade.titulo)} disabled={travado} onChange={(evento) => mudarCampo(atividade.titulo, evento.target.value)} />
                        </label>
                        <label className="campo-config campo-config-curto">
                            Tempo (segundos)
                            <input
                                type="number"
                                min={0}
                                placeholder="Sem cronômetro"
                                value={valorDoCampo(atividade.tempo)}
                                disabled={travado}
                                onChange={(evento) => mudarCampo(atividade.tempo, evento.target.value)}
                            />
                        </label>
                        <label className="campo-config">
                            Texto de abertura
                            <textarea rows={3} value={valorDoCampo(atividade.abertura)} disabled={travado} onChange={(evento) => mudarCampo(atividade.abertura, evento.target.value)} />
                        </label>
                        <label className="campo-config">
                            Texto de encerramento
                            <textarea rows={2} value={valorDoCampo(atividade.fim)} disabled={travado} onChange={(evento) => mudarCampo(atividade.fim, evento.target.value)} />
                        </label>
                        {seletorDeQuantidade(desejada, (quantidadeNova) => setQuantidade((atual) => ({ ...atual, [atividade.id]: quantidadeNova })))}

                        {Array.from({ length: desejada }, (_, i) => i + 1).map((k) => {
                            const existente = atividade.comuns.find((pergunta) => pergunta.k === k);
                            if (existente) {
                                return (
                                    <CartaoPergunta
                                        key={existente.id}
                                        titulo={`Pergunta ${k}`}
                                        idBase={`pergunta-${existente.id}`}
                                        valores={valoresDaPergunta(existente)}
                                        travado={travado}
                                        aoMudar={(mudanca) => mudarPergunta(existente, mudanca)}
                                        lixeira={existente.item?.pode_excluir && !travado ? {
                                            rotulo: `Excluir Pergunta ${k} da Atividade ${atividade.n}`,
                                            aoExcluir: () => void excluirNaHora(
                                                `Excluir a Pergunta ${k} da Atividade ${atividade.n}? Isso não pode ser desfeito.`,
                                                () => excluirItem({ entidade: 'pergunta', id: existente.id }, csrfToken),
                                            ),
                                        } : undefined}
                                    />
                                );
                            }
                            const chave = chaveDaNova(atividade.id, k);
                            const nova = perguntasNovas[chave] ?? perguntaVazia();
                            return (
                                <CartaoPergunta
                                    key={chave}
                                    titulo={`Pergunta ${k}`}
                                    idBase={`nova-${atividade.id}-${k}`}
                                    valores={nova}
                                    travado={travado}
                                    aoMudar={(mudanca) => setPerguntasNovas((atual) => ({ ...atual, [chave]: { ...nova, ...mudanca } }))}
                                />
                            );
                        })}
                        {atividade.bonus && (
                            <CartaoPergunta
                                titulo="Pergunta bônus"
                                idBase={`pergunta-${atividade.bonus.id}`}
                                valores={valoresDaPergunta(atividade.bonus)}
                                travado={travado}
                                aoMudar={(mudanca) => atividade.bonus && mudarPergunta(atividade.bonus, mudanca)}
                            />
                        )}
                    </section>
                );
            })}

            {atividadesNovas.map((nova, indice) => {
                const n = atividades.length + indice + 1;
                if (n !== marcada) return null;
                return (
                    <section key={nova.tempId} className="cartao-config cartao-config-novo" aria-labelledby={`atividade-nova-${nova.tempId}`}>
                        <div className="cabecalho-cartao-config">
                            <h3 id={`atividade-nova-${nova.tempId}`}>Atividade {n}</h3>
                            <button
                                type="button"
                                className="lixeira-config"
                                aria-label={`Descartar Atividade ${n}`}
                                onClick={() => setAtividadesNovas((atual) => atual.filter((outra) => outra.tempId !== nova.tempId))}
                            >
                                {LIXEIRA}
                            </button>
                        </div>
                        <label className="campo-config">
                            Título
                            <input type="text" value={nova.titulo} onChange={(evento) => mudarAtividadeNova(nova.tempId, (atual) => ({ ...atual, titulo: evento.target.value }))} />
                        </label>
                        <label className="campo-config campo-config-curto">
                            Tempo (segundos)
                            <input
                                type="number"
                                min={0}
                                placeholder="Sem cronômetro"
                                value={nova.tempo}
                                onChange={(evento) => mudarAtividadeNova(nova.tempId, (atual) => ({ ...atual, tempo: evento.target.value }))}
                            />
                        </label>
                        <p className="nota-config">Os textos de abertura e de encerramento ficam editáveis depois que a atividade for criada.</p>
                        {seletorDeQuantidade(nova.quantidade, (quantidadeNova) => mudarAtividadeNova(nova.tempId, (atual) => ({
                            ...atual,
                            quantidade: quantidadeNova,
                            perguntas: Array.from({ length: Math.max(atual.perguntas.length, quantidadeNova) }, (_, i) => atual.perguntas[i] ?? perguntaVazia()),
                        })))}
                        {nova.perguntas.slice(0, nova.quantidade).map((pergunta, i) => (
                            <CartaoPergunta
                                key={i}
                                titulo={`Pergunta ${i + 1}`}
                                idBase={`atividade-nova-${nova.tempId}-${i + 1}`}
                                valores={pergunta}
                                travado={false}
                                aoMudar={(mudanca) => mudarAtividadeNova(nova.tempId, (atual) => ({
                                    ...atual,
                                    perguntas: atual.perguntas.map((outra, j) => (j === i ? { ...outra, ...mudanca } : outra)),
                                }))}
                            />
                        ))}
                    </section>
                );
            })}

            <section className="cartao-config" aria-labelledby="config-pontuacao-lab">
                <div className="cabecalho-cartao-config">
                    <h3 id="config-pontuacao-lab">Pontuação</h3>
                    <button
                        type="button"
                        className={`seta-config${pontuacaoAberta ? ' seta-config-aberta' : ''}`}
                        aria-label={pontuacaoAberta ? 'Recolher Pontuação' : 'Abrir Pontuação'}
                        aria-expanded={pontuacaoAberta}
                        onClick={() => setPontuacaoAberta((aberta) => !aberta)}
                    >
                        {SETA}
                    </button>
                </div>
                {pontuacaoAberta && (
                    <div className="grade-pontuacao">
                        <label className="campo-config">
                            Tipo do Ranking
                            <select value={valorDaRegra('destino')} disabled={regrasTravadas} onChange={(evento) => setRegrasRascunho((atual) => ({ ...atual, destino: evento.target.value }))}>
                                <option value="grupo">Grupos</option>
                                <option value="individual">Individual</option>
                            </select>
                        </label>
                        <label className="campo-config">
                            Tipo do Ranking da pergunta bônus
                            <select value={valorDaRegra('destino_bonus')} disabled={regrasTravadas} onChange={(evento) => setRegrasRascunho((atual) => ({ ...atual, destino_bonus: evento.target.value }))}>
                                <option value="grupo">Grupos</option>
                                <option value="individual">Individual</option>
                            </select>
                        </label>
                        <p className="nota-config">Os pontos de cada pergunta ficam na própria pergunta.</p>
                    </div>
                )}
            </section>

            {aviso && (
                <p className={aviso.tipo === 'erro' ? 'mensagem-login aviso-cadastro' : 'mensagem-salvo'} role={aviso.tipo === 'erro' ? 'alert' : 'status'}>
                    {aviso.texto}
                </p>
            )}

            <div className="rodape-config">
                <button type="button" className="botao-cancelar-config" onClick={descartar} disabled={salvando}>
                    Cancelar
                </button>
                <button type="button" className="botao-salvar-config" onClick={() => void salvar()} disabled={salvando || travado}>
                    {salvando ? 'Salvando…' : 'Salvar Alterações'}
                </button>
            </div>
        </div>
    );
}
